import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';

import { KYCStatus } from '~/api/KYCStatus';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { SECOND } from '~/constants/variables';
import { useHeaderSize } from '~/hooks/useHeaderSize';
import { useIsTablet } from '~/pages/Order/hooks';

import { userStore } from '~/stores';

interface UseStickyWrapperOptions {
  elementRef: RefObject<HTMLDivElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  containerPaddingTop?: number;
  containerPaddingBottom?: number;
  headerGap?: number;
  headerId?: string;
}

const SAAS_CART_HEADER_HEIGHT = 150;

export const useStickyWrapper = (props: UseStickyWrapperOptions) => {
  const {
    elementRef,
    containerRef,
    containerPaddingTop = 0,
    containerPaddingBottom = 0,
    headerGap = 0,
  } = props;

  const isSaas = DEFAULT_COMPANIES.includes(company.name);

  const { headerHeight } = useHeaderSize();

  const [style, setStyle] = useState<CSSProperties>({});

  const isFixedRef = useRef<boolean>(false);
  const isFinishedRef = useRef<boolean>(false);
  const isInitializedRef = useRef<boolean>(false);

  const isTablet = useIsTablet();

  useEffect(() => {
    const element = elementRef.current;
    const container = containerRef.current;

    const listener = () => {
      if (!element || !container) {
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollTop =
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        window.scrollY;

      const scrollTopWithOffset = scrollTop + headerHeight + headerGap;
      const scrollBottom = scrollTopWithOffset + elementRect.height;

      const startPosition =
        container.offsetTop +
        containerPaddingTop +
        (isSaas ? SAAS_CART_HEADER_HEIGHT : 0);
      const endPosition =
        startPosition +
        (containerRect.height - containerPaddingTop - containerPaddingBottom);

      if (
        !isFixedRef.current &&
        !isFinishedRef.current &&
        isInitializedRef.current &&
        scrollTopWithOffset >= startPosition
      ) {
        isFixedRef.current = true;
        setStyle({
          position: 'fixed',
          top: isSaas ? headerGap : headerHeight + headerGap,
          width: elementRect.width,
        });
      }

      if (
        isFixedRef.current &&
        !isFinishedRef.current &&
        scrollTopWithOffset < startPosition
      ) {
        isFixedRef.current = false;
        setStyle({
          position: 'static',
        });
      }

      if (
        isFixedRef.current &&
        !isFinishedRef.current &&
        isInitializedRef.current &&
        scrollBottom >= endPosition
      ) {
        isFinishedRef.current = true;
        setStyle({
          position: 'absolute',
          bottom: containerPaddingBottom,
          width: elementRect.width,
        });
      }

      if (
        isFixedRef.current &&
        isFinishedRef.current &&
        isInitializedRef.current &&
        scrollBottom < endPosition
      ) {
        isFinishedRef.current = false;
        setStyle({
          position: 'fixed',
          top: isSaas ? headerGap : headerHeight + headerGap,
          width: elementRect.width,
        });
      }
    };

    listener();

    if (isTablet) {
      window.removeEventListener('scroll', listener);
      return;
    }

    window.addEventListener('scroll', listener);

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
    }

    return () => {
      window.removeEventListener('scroll', listener);
    };
  }, [isTablet]);

  return { style };
};

export const useUserVerificationStatusChecker = () => {
  const verificationStatusInterval = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const canCheckStatus =
    userStore.personalData.kycStatus !== KYCStatus.Verified &&
    userStore.isAuthorized;

  useEffect(() => {
    if (canCheckStatus) {
      userStore.requestPersonalData(true);
    }
  }, [canCheckStatus]);

  useEffect(() => {
    if (canCheckStatus) {
      verificationStatusInterval.current = setInterval(() => {
        userStore.requestPersonalData(true);
      }, 15 * SECOND);
    }
    return () => {
      if (verificationStatusInterval.current) {
        return clearInterval(verificationStatusInterval.current);
      }
    };
  }, [userStore.isAuthorized, userStore.personalData.kycStatus]);
};
